import React from "react";
import { MuscleDistribution } from "../../libs/exercises/types";
import {
  SELECTABLE_DISTRICTS,
  getMuscleColor,
} from "../WorkoutTables/muscleConfig";

type MuscleDistributionFormProps = {
  value: MuscleDistribution[];
  onChange: (value: MuscleDistribution[]) => void;
};

const PERCENTAGE_OPTIONS = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100];

export const MuscleDistributionForm: React.FC<MuscleDistributionFormProps> = ({
  value,
  onChange,
}) => {
  const selectedDistricts = value.map((v) => v.district);
  const total = value.reduce((sum, v) => sum + v.percentage, 0);
  const isSingle = value.length === 1;

  const toggleDistrict = (district: string) => {
    if (selectedDistricts.includes(district)) {
      const remaining = value.filter((v) => v.district !== district);
      // Se resta un solo distretto, forza 100%
      if (remaining.length === 1) {
        onChange([{ ...remaining[0], percentage: 100 }]);
      } else {
        onChange(remaining);
      }
    } else {
      // Aggiungi
      if (value.length === 0) {
        onChange([{ district, percentage: 100 }]);
      } else {
        // Aggiungi con 25% di default, senza toccare le percentuali esistenti
        onChange([...value, { district, percentage: 25 }]);
      }
    }
  };

  const updatePercentage = (district: string, percentage: number) => {
    onChange(
      value.map((v) => (v.district === district ? { ...v, percentage } : v))
    );
  };

  return (
    <div>
      <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
        Distribuzione Muscolare
      </label>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
        Seleziona i muscoli coinvolti e imposta la percentuale di coinvolgimento per ciascuno.
      </p>

      {/* Lista distretti con checkbox */}
      <div className="grid grid-cols-2 gap-1 mb-3">
        {SELECTABLE_DISTRICTS.map((district) => {
          const isSelected = selectedDistricts.includes(district);
          const color = getMuscleColor(district);
          return (
            <label
              key={district}
              className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-xs transition-colors ${
                isSelected
                  ? "bg-gray-100 dark:bg-gray-700 font-medium"
                  : "hover:bg-gray-50 dark:hover:bg-gray-800"
              }`}
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => toggleDistrict(district)}
                className="rounded border-gray-300 dark:border-gray-600 text-blue-500 w-3.5 h-3.5 dark:bg-gray-700"
              />
              <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: color }}
              />
              <span className="text-gray-700 dark:text-gray-300">
                {district}
              </span>
            </label>
          );
        })}
      </div>

      {/* Percentuali — mostra solo se 2+ muscoli selezionati */}
      {value.length > 1 && (
        <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-3 space-y-2">
          <div className="flex justify-between items-center mb-1">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-400 ">
              Percentuali
            </span>
            <span className="text-xs font-bold text-gray-500">
              Totale: {total}%
            </span>
          </div>
          {value.map((entry) => {
            const color = getMuscleColor(entry.district);
            return (
              <div
                key={entry.district}
                className="flex items-center gap-2"
              >
                <span
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: color }}
                />
                <span className="text-xs text-gray-700 dark:text-gray-300 w-32 truncate">
                  {entry.district}
                </span>
                <select
                  value={entry.percentage}
                  onChange={(e) =>
                    updatePercentage(entry.district, parseInt(e.target.value))
                  }
                  className="text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 dark:text-gray-200"
                >
                  {PERCENTAGE_OPTIONS.map((p) => (
                    <option key={p} value={p}>
                      {p}%
                    </option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>
      )}

      {/* Badge riepilogo se 1 solo muscolo */}
      {isSingle && (
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          → {value[0].district} al 100%
        </div>
      )}
    </div>
  );
};
